import type { MapRect } from './control-zones';
import type { PixelPoint } from './types';

/** `113586` §3e — a longer device name truncates to this many characters plus an ellipsis. */
export const LABEL_MAX_CHARS = 25;

export function truncateLabel(name: string): string {
  return name.length > LABEL_MAX_CHARS ? `${name.slice(0, LABEL_MAX_CHARS)}…` : name;
}

/** One device's name label as it would be drawn: the box it would occupy in map-area pixels. */
export type LabelCandidate = { id: string; rect: MapRect };

/**
 * Which name labels are drawn when they would collide (`113586` §3d). Colliding labels are
 * **suppressed, not offset**: an offset label points at a pin it no longer touches, and no design
 * exists for a leader line (grilling Q11).
 *
 * The winner is stable rather than emergent — candidates are considered in ascending id order — so
 * the same map always suppresses the same label, however the caller ordered its markers.
 *
 * Suppressing a label never hides a marker: this returns label ids only and leaves its input
 * untouched. That every marker stays individually visible and clickable is the renderer's half of
 * §3d, proven in `device-markers.test.tsx` rather than here.
 */
export function visibleLabels(candidates: readonly LabelCandidate[]): string[] {
  const kept: LabelCandidate[] = [];

  for (const candidate of [...candidates].sort((a, b) => compareIds(a.id, b.id))) {
    if (!kept.some((shown) => rectsOverlap(shown.rect, candidate.rect))) kept.push(candidate);
  }

  return kept.map((label) => label.id);
}

/** Codepoint order, and a proper comparator: equal ids return 0 rather than claiming an order. */
function compareIds(a: string, b: string): number {
  if (a < b) return -1;
  return a > b ? 1 : 0;
}

function rectsOverlap(a: MapRect, b: MapRect): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}

/**
 * The name-label chip's drawn geometry (Figma `823:701656`), in the pixels the collision arithmetic
 * needs. It lives here rather than in the renderer for the same reason {@link LABEL_MAX_CHARS} does:
 * the rule that decides which labels are drawn cannot be tested against a stylesheet.
 *
 * `charWidth` is the one estimate. Measuring text needs a laid-out DOM, which neither this module nor
 * the `node` project has — and a measurement pass per label per camera frame would cost more than the
 * suppression saves. 6.2 px is Geist Regular 11 px at its average advance.
 * `ponytail: average advance, not measured text — swap for a canvas `measureText` if a
 * proportionally-wide name ever suppresses a label it should not.`
 */
export const LABEL_CHIP = {
  /** Geist Regular 11 px, average advance. */
  charWidth: 6.2,
  /** `padding-inline: 8px` per side. */
  paddingX: 8,
  /** The chip's own height: 11 px text at `line-height: 1`, plus 5 px of padding twice. */
  height: 21,
  /**
   * The chip's bottom edge to the marker's point: half the 40 px **pin** plus the 4 px column gap.
   *
   * Note it is the drawn pin this measures from, not the 56 px hit box — the box extends 8 px further
   * up, so `device-markers.module.scss` pulls the chip back by exactly that slop to leave Figma's 4 px.
   * Change the gap there and this number moves with it.
   */
  pinOffset: 24,
} as const;

/**
 * One device's label as a box, centred over its marker. Pass the marker's projected point and its
 * display name; the name is {@link truncateLabel}d first, because a 60-character name occupies the
 * width of a 25-character one on screen and must collide like one.
 */
export function labelBox(point: PixelPoint, name: string): MapRect {
  const width = truncateLabel(name).length * LABEL_CHIP.charWidth + LABEL_CHIP.paddingX * 2;

  return {
    x: point.x - width / 2,
    y: point.y - LABEL_CHIP.pinOffset - LABEL_CHIP.height,
    width,
    height: LABEL_CHIP.height,
  };
}
